/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const PROTOCOL_CONFIG_DISCRIMINATOR = new Uint8Array([
  207, 91, 250, 28, 152, 179, 215, 209,
]);

export function getProtocolConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    PROTOCOL_CONFIG_DISCRIMINATOR,
  );
}

export type ProtocolConfig = {
  discriminator: ReadonlyUint8Array;
  /** Authority who can update the config (typically DAO or multisig) */
  authority: Address;
  /** Treasury wallet that receives protocol fees */
  treasury: Address;
  /** Buyback pool wallet for token buybacks (receives 20% of escrow fees) */
  buybackPool: Address;
  /** Moderator pool for dispute resolution fees */
  moderatorPool: Address;
  /**
   * Escrow completion fee in basis points (100 = 1%)
   * Default: 0 (disabled until mainnet)
   * Target: 50 (0.5%)
   */
  escrowFeeBps: number;
  /**
   * Agent registration fee in lamports
   * Default: 0 (disabled until mainnet)
   * Target: 10_000_000 (0.01 SOL)
   */
  agentRegistrationFee: bigint;
  /**
   * Marketplace listing fee in lamports
   * Default: 0 (disabled until mainnet)
   * Target: 1_000_000 (0.001 SOL)
   */
  listingFee: bigint;
  /**
   * Dispute resolution fee in basis points (100 = 1%)
   * Default: 0 (disabled until mainnet)
   * Target: 100 (1%)
   */
  disputeFeeBps: number;
  /**
   * Whether fees are enabled
   * Set to false during devnet, true after mainnet governance approval
   */
  feesEnabled: boolean;
  /** Timestamp when config was last updated */
  updatedAt: bigint;
  /** PDA bump seed */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

export type ProtocolConfigArgs = {
  /** Authority who can update the config (typically DAO or multisig) */
  authority: Address;
  /** Treasury wallet that receives protocol fees */
  treasury: Address;
  /** Buyback pool wallet for token buybacks (receives 20% of escrow fees) */
  buybackPool: Address;
  /** Moderator pool for dispute resolution fees */
  moderatorPool: Address;
  /**
   * Escrow completion fee in basis points (100 = 1%)
   * Default: 0 (disabled until mainnet)
   * Target: 50 (0.5%)
   */
  escrowFeeBps: number;
  /**
   * Agent registration fee in lamports
   * Default: 0 (disabled until mainnet)
   * Target: 10_000_000 (0.01 SOL)
   */
  agentRegistrationFee: number | bigint;
  /**
   * Marketplace listing fee in lamports
   * Default: 0 (disabled until mainnet)
   * Target: 1_000_000 (0.001 SOL)
   */
  listingFee: number | bigint;
  /**
   * Dispute resolution fee in basis points (100 = 1%)
   * Default: 0 (disabled until mainnet)
   * Target: 100 (1%)
   */
  disputeFeeBps: number;
  /**
   * Whether fees are enabled
   * Set to false during devnet, true after mainnet governance approval
   */
  feesEnabled: boolean;
  /** Timestamp when config was last updated */
  updatedAt: number | bigint;
  /** PDA bump seed */
  bump: number;
  /** Reserved for future use */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link ProtocolConfigArgs} account data. */
export function getProtocolConfigEncoder(): FixedSizeEncoder<ProtocolConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["authority", getAddressEncoder()],
      ["treasury", getAddressEncoder()],
      ["buybackPool", getAddressEncoder()],
      ["moderatorPool", getAddressEncoder()],
      ["escrowFeeBps", getU16Encoder()],
      ["agentRegistrationFee", getU64Encoder()],
      ["listingFee", getU64Encoder()],
      ["disputeFeeBps", getU16Encoder()],
      ["feesEnabled", getBooleanEncoder()],
      ["updatedAt", getI64Encoder()],
      ["bump", getU8Encoder()],
      ["reserved", fixEncoderSize(getBytesEncoder(), 64)],
    ]),
    (value) => ({ ...value, discriminator: PROTOCOL_CONFIG_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link ProtocolConfig} account data. */
export function getProtocolConfigDecoder(): FixedSizeDecoder<ProtocolConfig> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["authority", getAddressDecoder()],
    ["treasury", getAddressDecoder()],
    ["buybackPool", getAddressDecoder()],
    ["moderatorPool", getAddressDecoder()],
    ["escrowFeeBps", getU16Decoder()],
    ["agentRegistrationFee", getU64Decoder()],
    ["listingFee", getU64Decoder()],
    ["disputeFeeBps", getU16Decoder()],
    ["feesEnabled", getBooleanDecoder()],
    ["updatedAt", getI64Decoder()],
    ["bump", getU8Decoder()],
    ["reserved", fixDecoderSize(getBytesDecoder(), 64)],
  ]);
}

/** Gets the codec for {@link ProtocolConfig} account data. */
export function getProtocolConfigCodec(): FixedSizeCodec<
  ProtocolConfigArgs,
  ProtocolConfig
> {
  return combineCodec(getProtocolConfigEncoder(), getProtocolConfigDecoder());
}

export function decodeProtocolConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<ProtocolConfig, TAddress>;
export function decodeProtocolConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<ProtocolConfig, TAddress>;
export function decodeProtocolConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<ProtocolConfig, TAddress> | MaybeAccount<ProtocolConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getProtocolConfigDecoder(),
  );
}

export async function fetchProtocolConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<ProtocolConfig, TAddress>> {
  const maybeAccount = await fetchMaybeProtocolConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeProtocolConfig<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<ProtocolConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeProtocolConfig(maybeAccount);
}

export async function fetchAllProtocolConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<ProtocolConfig>[]> {
  const maybeAccounts = await fetchAllMaybeProtocolConfig(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeProtocolConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<ProtocolConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeProtocolConfig(maybeAccount),
  );
}

export function getProtocolConfigSize(): number {
  return 230;
}
